import { useTenantAvailable } from '@api/application-configuration';
import { useLogoutMutation } from '@api/auth';
import {
  Goal,
  Info,
  List,
  LogOut,
  RefreshCcw,
  Settings2,
  Share2,
  ShieldUser,
  User
} from '@tamagui/lucide-icons';
import { Pressable } from '@unistocks/components/basic/pressable';
import { ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { Group } from '@unistocks/components/group/group';
import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { Page } from '@unistocks/components/page/page';
import { AboutModal } from '@unistocks/components/settings/about-modal';
import { MenuItem } from '@unistocks/components/settings/menu-item';
import { Heading } from '@unistocks/components/text/text';
import { useDefaultSavingsGoalsRoute } from '@unistocks/features/savings-goals/use-route';
import { t } from '@unistocks/localization';
import { useProfileGetQuery } from '@unistocks/proxy/axios-client/ProfileQuery';
import { useDevToolsEnabled } from '@unistocks/shared/hooks/use-feature';
import { applicationName } from 'expo-application';
import { Href, useRouter } from 'expo-router';
import { useState } from 'react';
import { Avatar, Separator } from 'tamagui';

export const Menu: React.FC = () => {
  const isTenantUser = useTenantAvailable();
  const router = useRouter();
  const isDevToolsEnabled = useDevToolsEnabled();
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const { data: profile } = useProfileGetQuery();
  const savingsGoalsHref = useDefaultSavingsGoalsRoute();

  const editProfileHref: Href = isTenantUser
    ? '/(tabs)/(menu)/settings/edit-profile'
    : '/(admin)/(menu)/settings/edit-profile';

  const settingsHref: Href = isTenantUser ? '/(tabs)/(menu)/settings' : '/(admin)/(menu)/settings';

  const { mutate: logout, isPending: isLoadingLogout } = useLogoutMutation();

  return (
    <Page>
      <Page.ScrollView
        headerBackArrow={false}
        contentContainerStyle={ScrollViewStyles.contentContainerStyle}
      >
        <Pressable withFeedback withScale onPress={() => router.navigate(editProfileHref)}>
          <Avatar circular size="$12" mx={'auto'}>
            <Avatar.Image
              accessibilityLabel={`${profile?.name ?? ''} ${profile?.surname ?? ''}`}
              src={profile?.extraProperties.ProfilePictureUrl}
            />
            <Avatar.Fallback theme={'surface3'} backgroundColor={'$background'}>
              <User color={'$unistocks'} size={'$12'} />
            </Avatar.Fallback>
          </Avatar>
        </Pressable>
        <Heading textAlign="center" mt={'$3'}>
          {profile?.name} {profile?.surname}
        </Heading>

        <Page.Content p={0} mt={'$5'}>
          <Group separator={<Separator />}>
            {isTenantUser ? (
              <MenuItem
                title={t('::Menu:SavingsGoals')}
                subTitle={t('::Menu:SavingsGoals:Description')}
                icon={<Goal color={'$success'} />}
                onPress={() => router.navigate(savingsGoalsHref)}
              />
            ) : null}

            {isTenantUser ? (
              <MenuItem
                title={t('::ExternalAccounts')}
                subTitle={t('::Settings:ExternalAccounts:Description')}
                icon={<RefreshCcw color={'$purple10'} />}
                onPress={() => router.navigate('/(tabs)/(menu)/external-accounts')}
              />
            ) : null}

            {isTenantUser ? (
              <MenuItem
                title={t('::Menu:SharedPortfolios')}
                subTitle={t('::Menu:SharedPortfolios:Description')}
                icon={<Share2 color={'$bodyFont'} />}
                onPress={() => router.navigate('/(tabs)/(menu)/shared-portfolios')}
              />
            ) : null}

            {!isTenantUser ? (
              <MenuItem
                title={t('AbpIdentity::Roles')}
                subTitle={t('AbpIdentity::ManageRoles')}
                icon={<ShieldUser color={'$success'} />}
                onPress={() => router.navigate('/(admin)/role-management')}
              />
            ) : null}

            <MenuItem
              title={t('::Menu:Settings')}
              subTitle={t('::Menu:Settings:Description')}
              icon={<Settings2 color={'$unistocks'} />}
              onPress={() => router.navigate(settingsHref)}
            />

            {isDevToolsEnabled ? (
              <MenuItem
                title={'Sitemap'}
                subTitle={'Sitemap'}
                icon={<List color={'$gray11'} />}
                onPress={() => router.navigate('/_sitemap')}
              />
            ) : null}

            <MenuItem
              title={t('::Settings:About', { 0: applicationName })}
              subTitle={t('::Settings:About', { 0: applicationName })}
              icon={<Info color={'$blue10'} />}
              onPress={() => setAboutModalOpen(true)}
            />

            <MenuItem
              title={t('::Logout')}
              subTitle={t('::Logout')}
              icon={<LogOut color={'$gray10'} />}
              iconAfter={isLoadingLogout ? <Spinner /> : undefined}
              onPress={() => logout()}
            />
          </Group>
        </Page.Content>
      </Page.ScrollView>

      <AboutModal open={aboutModalOpen} onOpenChange={setAboutModalOpen} />
    </Page>
  );
};

Menu.displayName = 'ProfileMenuScreen';
